/**
 * Pure helpers explaining how the buying-table "Historical Daily Sales"
 * (units/day) value is derived, for the Product Replenishment Analysis modal.
 *
 * Mirrors assisty-bot olap_inventory_replenishment_product_variant.yml:
 *   average_daily_units_sold = round(sold_quantity / days, 2)
 *   days (include OOS) = clamped calendar days == [period_days]
 *   days (exclude OOS) = period_days - out_of_stock_days
 *   toggle: out_of_stock_days_excluding === 'yes' selects the exclude variant —
 *     BUT once out_of_stock_days >= 80% of the period the backend falls back to
 *     all days (the >=80%-OOS threshold), so an exclude-ON product can still be
 *     measured over all days. We detect that and label it as a fallback.
 * period_days is the raw, un-thresholded calendar span; we prefer it over
 * in_stock_days (which the backend now caps at the full period in the fallback).
 * The displayed average_daily_units_sold_param adds a bundle term, so a pure
 * units/days reconstruction only reconciles for non-bundle products.
 */

export interface ISalesRateExplanationInput {
  unitsSold: number;          // [sold_quantity_param]
  inStockDays: number;        // [in_stock_days] (backend, capped at period in the OOS fallback)
  outOfStockDays: number;     // [out_of_stock_days]
  periodDays?: number;        // [period_days] raw calendar span; falls back to inStock+oos if absent
  displayedRate: number;      // [average_daily_units_sold_param]
  oosExcludedHint: boolean;   // out_of_stock_days_excluding === 'yes'
  rangeStart?: string | null; // selected window start (this.dates[0])
  rangeEnd?: string | null;   // selected window end   (this.dates[1])
}

export interface ISalesRateExplanation {
  unitsSold: number;
  periodDays: number;         // inStockDays + outOfStockDays (publish-clamped)
  sellingDays: number;        // denominator actually used
  oosDays: number;
  oosExcluded: boolean;
  oosFellBack: boolean;       // setting ON but OOS >= 80% of period -> backend used all days
  displayedRate: number;
  reconstructedRate: number;
  reconciles: boolean;
  hasBundleContribution: boolean;
  isPeriodClamped: boolean;   // effective period shorter than the selected window
  periodStartFormatted: string | null;
  periodEndFormatted: string | null;
}

const EPSILON = 0.005;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function isOosExcluded(reportParams: any): boolean {
  const items: any[] = reportParams?.items
    || (Array.isArray(reportParams) ? reportParams : []);
  const p = items.find((x: any) => (x?.code || '') === 'out_of_stock_days_excluding');
  if (!p) {
    return false; // backend default: include OOS days
  }
  const v = Array.isArray(p.value) ? p.value[0] : p.value;
  return String(v) === 'yes';
}

function parseUtc(d?: string | null): Date | null {
  if (!d) {
    return null;
  }
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
}

function fmtDate(dt: Date | null): string | null {
  if (!dt || isNaN(dt.getTime())) {
    return null;
  }
  // Format in UTC: inputs are date-only (YYYY-MM-DD) and parse as UTC midnight,
  // so formatting in UTC keeps the calendar date stable across timezones.
  return dt.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

export function computeSalesRateExplanation(
  input: ISalesRateExplanationInput,
): ISalesRateExplanation {
  const unitsSold = Math.max(0, Number(input.unitsSold) || 0);
  const reportedInStockDays = Math.max(0, Number(input.inStockDays) || 0);
  const oosDays = Math.max(0, Number(input.outOfStockDays) || 0);
  const displayedRate = round2(Number(input.displayedRate) || 0);

  const rangeStart = parseUtc(input.rangeStart);
  const rangeEnd = parseUtc(input.rangeEnd);
  const selectedWindowDays = (rangeStart && rangeEnd)
    ? Math.round((rangeEnd.getTime() - rangeStart.getTime()) / MS_PER_DAY) + 1
    : 0;

  // True calendar span. Prefer the backend [period_days] — raw date_diff+1,
  // identical per location and publish-clamped, so it's exact.
  // Without it, fall back to min(selected window, in_stock + OOS):
  //   - in_stock + OOS is NOT the period for multi-location rows. in_stock_days
  //     and out_of_stock_days are averaged across locations independently, so
  //     their sum OVERSHOOTS the window (more so once the >=80%-OOS cap raises
  //     in_stock to the full period) — capping at the selected window fixes that;
  //   - but in_stock + OOS UNDERSHOOTS for a new/limited-history product (shorter
  //     than the picked window) — keeping the min preserves that clamp.
  const rawPeriod = Math.round(Number(input.periodDays) || 0);
  const inStockPlusOos = reportedInStockDays + oosDays;
  const periodDays = rawPeriod > 0
    ? rawPeriod
    : (selectedWindowDays > 0 ? Math.min(selectedWindowDays, inStockPlusOos) : inStockPlusOos);

  // The two denominators the backend could have divided by:
  //   exclude OOS              -> in-stock days = period - OOS (what exclusion WOULD use)
  //   include / high-OOS fallback -> all days in the period
  const excludeDays = Math.max(1, periodDays - oosDays);
  const includeDays = Math.max(1, periodDays);
  const rateExclude = round2(unitsSold / excludeDays);
  const rateInclude = round2(unitsSold / includeDays);

  const excludeMatches = Math.abs(rateExclude - displayedRate) < EPSILON;
  const includeMatches = Math.abs(rateInclude - displayedRate) < EPSILON;

  let oosExcluded: boolean;
  let reconciles: boolean;
  if (excludeMatches && includeMatches) {
    // identical denominators (oosDays === 0) — label using the param hint
    oosExcluded = input.oosExcludedHint;
    reconciles = true;
  } else if (excludeMatches) {
    oosExcluded = true;
    reconciles = true;
  } else if (includeMatches) {
    oosExcluded = false;
    reconciles = true;
  } else {
    // bundle contribution / edge case — trust the hint, hide the equation
    oosExcluded = input.oosExcludedHint;
    reconciles = false;
  }

  // Setting is ON, there ARE out-of-stock days, yet the rate reconciles to the
  // all-days denominator: the backend hit the >=80%-OOS threshold and fell back
  // to all days (excluding them would divide by a near-zero denominator and
  // over-inflate the rate). Surface that instead of mislabeling it "included".
  const oosFellBack = input.oosExcludedHint && reconciles && !oosExcluded && oosDays > 0;

  const sellingDays = oosExcluded ? excludeDays : includeDays;
  const reconstructedRate = round2(unitsSold / sellingDays);

  // The period ENDS at rangeEnd and spans periodDays. Derive the start from the
  // end so the displayed date range ALWAYS matches the day count — even when the
  // product's history is shorter than the selected window (new product / limited
  // tracking). Showing the raw picker start next to a clamped count would
  // contradict itself.
  let periodStartFormatted: string | null = null;
  let periodEndFormatted: string | null = null;
  let isPeriodClamped = false;
  if (rangeEnd && periodDays > 0) {
    periodEndFormatted = fmtDate(rangeEnd);
    periodStartFormatted = fmtDate(new Date(rangeEnd.getTime() - (periodDays - 1) * MS_PER_DAY));
    if (selectedWindowDays > 0) {
      isPeriodClamped = periodDays < selectedWindowDays;
    }
  }

  return {
    unitsSold,
    periodDays,
    sellingDays,
    oosDays,
    oosExcluded,
    oosFellBack,
    displayedRate,
    reconstructedRate,
    reconciles,
    hasBundleContribution: !reconciles,
    isPeriodClamped,
    periodStartFormatted,
    periodEndFormatted,
  };
}
